import type { Emitter } from "@core/events/emitter";

export type PendingImportStatus =
  | "uploading"
  | "converting"
  | "downloading"
  | "importing"
  | "done";

/**
 * A model import currently in flight — surfaced as a transient loading row in
 * the hierarchy (Unity keys the equivalent placeholder by the importer's
 * `TaskID`). `id` is the conversion guid; `displayName` is the file name;
 * `progress` (0..1) drives the row's fill bar; `sceneId` scopes the row to the
 * scene it was started in (Unity only shows it in the active scene).
 */
export interface PendingImport {
  readonly id: string;
  readonly displayName: string;
  readonly progress: number;
  readonly status: PendingImportStatus;
  readonly sceneId: string | undefined;
}

/**
 * `IPendingImportsModel` — registry of in-flight model imports (cloud CAD
 * conversion). Mirrors the {@link IToastsModel} surface. Driven by
 * `PixyzConversionModel`; consumed by the hierarchy loading indicator via
 * `usePendingImports`.
 */
export interface IPendingImportsModel {
  readonly onChanged: Emitter<ReadonlyArray<PendingImport>>;
  readonly pendingImports: ReadonlyArray<PendingImport>;

  begin(input: { id: string; displayName: string; sceneId?: string }): void;
  update(id: string, progress: number, status?: PendingImportStatus): void;
  remove(id: string): void;
}

export const PENDING_IMPORTS_MODEL = Symbol.for("Campfire.PendingImportsModel");
